import {
	Button,
	Col,
	DatePicker,
	Popover,
	Row,
	Select,
	Switch,
} from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { Navbar } from "../Navigation/Navbar";
import { StatTileV2 } from "../StatTileV2/StatTileV2";
import { CompactStatTile } from "../StatTileV2/CompactStatTile";
import { useState, useMemo } from "react";
import { trainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import { getTeamOverviewDefaults, setTeamPresets, setTeamFilter, getTeamGPSData } from "../../store/actions/team.actions";
import { LoadingComponent } from "../Common/LoadingComponent";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../Utils/date_utils";
import { ChartWithSelect } from "../../Components/ChartsWithSelect/ChartWithSelect.jsx"
import BarChart from '../../Components/Charts/BarChart';
import { mapDataForOverviewV2Chart } from '../../mappers/model/BarChartMapper';
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { useDataAccess, PermissionedButton } from "@components/PermissionedComponents";
import { withLockedStreamGroups } from "@components/PermissionedComponents/lockedStreamGroups";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import '../../Styles/styles-OverviewV2.css'

export const OverviewV2 = () => {
	const PRESET_TYPE = PresetType.Overview;
	const DEFAULT_PRESET = 'Default'

	const dispatch = useDispatch();
	const {
		overviewStats,
		misc: { comparison_types },
		dropdowns: {player: playerDropdowns},
		loading: { overviewStats: loading },
		all_metric_types,
		presets,
		positions,
		playerGroups: allPlayerGroups,
	} = useSelector(({ team }) => team);

	const {
		user,
	  } = useAuth0();

	const [metricKeys, setMetricKeys] = useState(new Array(16).fill(""));
	const [tileChartKeyChange, setTileChartKeyChange] = useState(['tile', null]);
	const [tileKeys, setTileKeys] = useState(new Array(8).fill(""));
	const [chartKeys, setChartKeys] = useState(new Array(8).fill(""));
	const [stat, setStat] = useState("Avg");
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
	const [pending, setPending] = useState(false);
	const [startDate, setStartDate] = useState(dayjs().subtract(1, "month"));
	const [endDate, setEndDate] = useState(dayjs());
	const [showGameDays, setShowGameDays] = useState(false);
	const [showWeekly, setShowWeekly] = useState(false);
	const [loadingChart, setLoadingChart] = useState(false);
	const [chartData, setChartData] = useState({ data: [], metrics: [] });
	const [unsavedChanges, setUnsavedChanges] = useState(false);
	const [teamHasMappedQuestions, setTeamHasMappedQuestions] = useState(false);
	const [position, setPosition] = useState([])
	const [playerGroup, setPlayerGroup] = useState([])
	
	useMetricDropdowns(['testing', 'forceplate', 'gps', 'wellness']);

	const { canAccess, ready: permissionsReady } = useDataAccess();

	// The dropdowns endpoint omits streams the user cannot read, so locked ones are
	// put back as a heading with a single unclickable row.
	const overviewDropdowns = useMemo(
		() => withLockedStreamGroups(playerDropdowns, canAccess, all_metric_types),
		[playerDropdowns, canAccess, all_metric_types]
	);

	useEffect(() => {
		// The overview chart and team GPS data both 403 without AccessGpsData.
		if (!permissionsReady) return;

		dispatch(getTeamOverviewDefaults());
		dispatch(setTeamFilter({ position: [] }));
		setPosition([]);
		dispatch(setTeamFilter({ player_group: [] }));
		dispatch(setTeamFilter({ startDate: dayjs().subtract(1, "month").format("YYYY-MM-DD") }));
		dispatch(setTeamFilter({ endDate: dayjs().format("YYYY-MM-DD") }));
		setPlayerGroup([]);

		if (canAccess("gps")) {
			fetchChartData();
			dispatch(getTeamGPSData());
		}
	}, [permissionsReady]);

	useEffect(() => {
		if (playerDropdowns && Object.entries(playerDropdowns).length > 0) {
			const hasWellnessWithChildren = playerDropdowns.some(
				item => item.key === "wellness" && Array.isArray(item.children) && item.children.length > 0
			);
			setTeamHasMappedQuestions(hasWellnessWithChildren);
		}

	}, [playerDropdowns])

	const saveTiles = async () => {
		setPending(true);
		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateOverviewDefaultMetrics(metricKeys);
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: metricKeys, preset_id: presetModified.preset_id
				});
				presetModified.config = metricKeys
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved tiles");
		} catch (e) {
			message.error("Error saving tiles");
		}
		setPending(false);
	};

	const handleChangeDateRange = (dates) => {
		// antd passes null when the range is cleared. Keep the current range.
		if (!dates?.[0] || !dates?.[1]) return;
		const [startDate, endDate] = dates;
		setStartDate(startDate);
		setEndDate(endDate);
		//using team trainings api for chart corresp tile data 
		dispatch(setTeamFilter({ startDate: startDate.format("YYYY-MM-DD") }));
		dispatch(setTeamFilter({ endDate: endDate.format("YYYY-MM-DD") }));
	};

	const setSelectedPosition = (value) => {
		if (position.includes(value)) {
			const newPositions = position.filter((pos) => pos !== value);
			setPosition(newPositions);
			dispatch(setTeamFilter({ position: newPositions }));
		} else {
			setPosition(value);
			dispatch(setTeamFilter({ position: value }));
		}
	};

	useEffect(() => {
		if (!permissionsReady || !canAccess("gps")) return;
		fetchChartData();
		dispatch(getTeamGPSData());
	}, [position, playerGroup, permissionsReady])

	const setSelectedPlayerGroups = (val) => {
		if (playerGroup.includes(val)) {
			const newPlayerGroups = playerGroup.filter((pos) => pos !== val);
			setPlayerGroup(newPlayerGroups);
			dispatch(setTeamFilter({ player_group: newPlayerGroups }));
		} else {
			setPlayerGroup(val);
			dispatch(setTeamFilter({ player_group: val }));
		}
	}

	const fetchChartData = async () => {
		if (!canAccess("gps")) return;
		setLoadingChart(true);
		try {
			const data = await trainingAPI.getOverviewChartV2({
				start_date: startDate.format(DATE_FORMAT),
				end_date: endDate.format(DATE_FORMAT),
				weekly: showWeekly,
				game_days_only: showGameDays,
				positions: position,
				player_groups: playerGroup
			});
			setChartData(data);
		} catch (e) {}
		setLoadingChart(false);
	}

	useEffect(() => {
		if (metricKeys) {
			setTileKeys(metricKeys.slice(0, 8));
			setChartKeys(metricKeys.slice(8, 16));
		}
	}, [metricKeys])

	// useEffect(() => {
	// 	if (tileKeys) {
	// 		// const [changeType, index] = tileChartKeyChange;
	// 		// chart keys will be updated only if a tile key changes but not vice versa
	// 		// if (changeType === 'tile') {
	// 			if (index !== null) {
	// 				chartKeys[index] = tileKeys[index];
	// 			}
	// 			for (let i = 0; i < chartKeys.length; i++) {
	// 				if (!chartKeys[i]) {
	// 					chartKeys[i] = tileKeys[i];
	// 				}
	// 			}

	// 			setChartKeys(chartKeys);
	// 		// }
	// 	}
	// }, [tileKeys])

	const handleChangeTileKeys = (index) => (value) => {
		const newTileKeys = [...tileKeys];
		newTileKeys[index] = value;
		setTileKeys(newTileKeys);
		const newChartKeys = [...chartKeys];
		newChartKeys[index] = value;
		setChartKeys(newChartKeys);

		setMetricKeys([...newTileKeys, ...newChartKeys]);
		setUnsavedChanges(true);
	};

	const handleChartDDClick = (index) => (value) => {
		const newChartKeys = [...chartKeys];
		if (typeof value === 'object' ) {
			newChartKeys[index] = value?.key;
		} else {
			newChartKeys[index] = value;
		}
		setChartKeys(newChartKeys);
		setMetricKeys([...tileKeys, ...newChartKeys]);
	};

	return (
		<>
			<Navbar />
			<div
				style={{
					margin: "1rem",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 16,
				}}
			>
				<DatePicker.RangePicker
					value={[startDate, endDate]}
					onChange={handleChangeDateRange}
					allowClear={false}
				/>
				<Select
					showSearch
					optionFilterProp="value"
					allowClear
					placeholder="Full Team"
					mode="multiple"
					onChange={setSelectedPosition}
					style={{ width: 300, marginLeft: "0.5em" }}
					defaultValue={position}
					>
					{positions.map((pos, i) => (
						<Option key={i} value={pos}>
						{pos}
						</Option>
					))}
				</Select>
				<Select
					allowClear
					showSearch
					optionFilterProp="value"
					placeholder="All Groups"
					value={playerGroup}
					style={{ width: 150, marginLeft: "0.5em" }}
					onChange={setSelectedPlayerGroups}
					mode="multiple"
					>
					{allPlayerGroups.map((playerGroup, i) => (
						<Option key={i} value={playerGroup?.player_group_id}>
						{playerGroup?.name}
						</Option>
					))}
				</Select>
				{/* <Popover
					content={
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								gap: 16,
								width: 140,
							}}
						>
							<Switch
								checkedChildren="Game Days"
								unCheckedChildren="All Days"
								value={showGameDays}
								onChange={(val) => setShowGameDays(val)}
							/>
						</div>
					}
					title="Day Options"
					trigger="click"
				>
					<Button>Day Options</Button>
				</Popover> */}
				<PermissionedButton
					type="primary"
					onClick={() => {
						fetchChartData();
						dispatch(getTeamGPSData());
					}}
					loading={loadingChart}
					permissionCheck="AccessGpsData"
				>
					Search
				</PermissionedButton>
			</div>
			{
				teamHasMappedQuestions && (
					<LoadingComponent loading={loadingChart} title="Loading chart...">
						<div style={{display: 'flex', justifyContent: 'center'}}>
							<div style={{width: '95%'}}>
								{
									chartData && chartData?.metrics?.length && (
										<BarChart 
											options={mapDataForOverviewV2Chart(chartData)}
											height={280}
										/>
									)
								}
							</div>
						</div>
					</LoadingComponent>
				)
			}
			<LoadingComponent loading={loading} title="Loading metrics...">
				<div
					style={{
						minHeight: "100%",
						margin: "1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<div className="exportable tab-testing testing-stats testing-stats-toggles flex gap-16 mb-16 items-center w-full justify-center">
						<PresetSelection 
							presets={presets} 
							user={user} 
							presetType={PRESET_TYPE} 
							statDefaults={overviewStats}
							currStatMetrics={metricKeys}
							setStatMetrics={setMetricKeys} 
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={option}
							setComparisonOption={setOption}
							handleSave={saveTiles}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
						/>
					</div>

					<Row
						gutter={[8, 12]}
						style={{ width: "90%"}}
						className="exportable tab-testing testing-stats"
					>
						{tileKeys && new Array(8).fill(0).map((_, i) => (
							<Col span={6} key={i} style={{ display: 'flex', justifyContent: 'center' }}>
								<CompactStatTile
									dropdowns={overviewDropdowns}
									metric_identifier_with_stat={tileKeys[i]}
									onChange={handleChangeTileKeys(i)}
									option={option}
									endDate={endDate?.format(DATE_FORMAT)}
									startDate={startDate?.format(DATE_FORMAT)}
									isCompact={true}
									type={"team"}
									comparison={option}
									positionFilter={position}
								/>

							</Col>
						))}

						{chartKeys && new Array(8).fill(0).map((_, i) => (
							<Col 
								key={`chart-col-${i}`}
								span={12} 
								style={{
									display: 'flex',
									justifyContent: 'center'
								}}
							>
								<ChartWithSelect 
									chartIndex={i}
									selectedMetricKey={chartKeys[i]} 
									handleChartDropdownChange={handleChartDDClick}
									stat={stat}
									startDate={startDate}
									endDate={endDate}
									playerGroup={playerGroup}
									position={position}
								/>
							</Col>
						))}
					</Row>
				</div>
			</LoadingComponent>
		</>
	);
};
